import { UserProfile, Permission, rolePermissions } from '../types/UserProfile';
import ErrorLoggingService from './errorLoggingService';

class PermissionService {
  static hasPermission(userProfile: UserProfile | null, requiredPermission: Permission): boolean {
    if (!userProfile) {
      ErrorLoggingService.logError(
        'Permission check failed: No user profile',
        'low',
        undefined,
        'permission_check'
      );
      return false;
    }

    try {
      // Check if user has the specific permission
      const hasPermission = userProfile.permissions.includes(requiredPermission);
      
      if (!hasPermission) {
        ErrorLoggingService.logError(
          `Permission denied: ${requiredPermission}`,
          'low',
          userProfile.id,
          'permission_check',
          { requiredPermission, userRole: userProfile.role }
        );
      }
      
      return hasPermission;
    } catch (error) {
      ErrorLoggingService.logError(
        error,
        'high',
        userProfile.id,
        'permission_check_error'
      );
      return false;
    }
  }

  static hasAnyPermission(userProfile: UserProfile | null, requiredPermissions: Permission[]): boolean {
    if (!userProfile) return false;
    return requiredPermissions.some(permission => this.hasPermission(userProfile, permission));
  }

  static hasAllPermissions(userProfile: UserProfile | null, requiredPermissions: Permission[]): boolean {
    if (!userProfile) return false;
    return requiredPermissions.every(permission => this.hasPermission(userProfile, permission));
  }

  static getPermissionsForRole(role: UserProfile['role']): Permission[] {
    return rolePermissions[role] || [];
  }

  static validateUserPermissions(userProfile: UserProfile): boolean {
    try {
      // Check if user's permissions match their role
      const roleBasedPermissions = this.getPermissionsForRole(userProfile.role);
      const hasValidPermissions = userProfile.permissions.every(permission => 
        roleBasedPermissions.includes(permission)
      );

      if (!hasValidPermissions) {
        ErrorLoggingService.logError(
          'Invalid permission configuration detected',
          'high',
          userProfile.id,
          'permission_validation',
          { 
            userRole: userProfile.role,
            invalidPermissions: userProfile.permissions.filter(p => !roleBasedPermissions.includes(p))
          }
        );
      }

      return hasValidPermissions;
    } catch (error) {
      ErrorLoggingService.logError(
        error,
        'high',
        userProfile.id,
        'permission_validation_error'
      );
      return false;
    }
  }
}

export default PermissionService; 